import React from 'react';
import { Loader2, Palette } from 'lucide-react';

interface ArtDirectionCardProps {
  artDirection: Record<string, any> | undefined;
  isGenerating: boolean;
}

const ArtDirectionCard: React.FC<ArtDirectionCardProps> = ({ artDirection, isGenerating }) => {
  if (!artDirection && !isGenerating) return null;

  if (isGenerating) {
    return (
      <div className="rounded-xl border-2 border-dashed border-primary-200 bg-white p-5">
        <div className="flex items-center gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <Loader2 className="h-5 w-5 animate-spin text-brand-purple" />
          <span className="text-sm font-medium text-brand-purple">Marco is crafting your visual direction...</span>
        </div>
      </div>
    );
  }

  const direction = artDirection!;
  const keywords: string[] = Array.isArray(direction.globalStyleKeywords) ? direction.globalStyleKeywords : [];
  const deliverables: Array<Record<string, any>> = Array.isArray(direction.deliverables) ? direction.deliverables : [];

  return (
    <div className="rounded-xl border border-primary-100 bg-white p-5 shadow-card">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="h-4 w-4 text-brand-purple" />
        <h3 className="font-heading text-[15px] font-bold text-text-dark">Art Direction</h3>
      </div>

      {direction.directionStatement && (
        <div className="rounded-lg bg-primary-50 p-3 mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-purple mb-1">Direction</div>
          <div className="text-xs text-text-dark leading-relaxed">{direction.directionStatement}</div>
        </div>
      )}

      {keywords.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {keywords.map((kw: string) => (
            <span key={kw} className="rounded-full bg-primary-200 px-2 py-0.5 text-[10px] font-medium text-brand-purple">{kw}</span>
          ))}
        </div>
      )}

      {deliverables.length > 0 && (
        <div className="space-y-3">
          {deliverables.slice(0, 4).map((d, i) => {
            const fields = [
              { label: 'Lighting', value: d.lighting },
              { label: 'Composition', value: d.composition },
              { label: 'Mood', value: d.mood },
              { label: 'Realism', value: d.realismGuidance },
            ].filter((f) => f.value);
            return (
              <div key={i}>
                <div className="grid grid-cols-2 gap-2">
                  {fields.map((f) => (
                    <div key={f.label} className="rounded-lg bg-gray-50 p-2.5">
                      <div className="text-[9px] font-semibold uppercase tracking-wider text-body-grey mb-0.5">{f.label}</div>
                      <div className="text-[11px] text-text-dark">{f.value}</div>
                    </div>
                  ))}
                </div>
                {d.scenePrompt && (
                  <div className="mt-2 rounded-lg bg-gray-50 p-2.5">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-body-grey mb-0.5">Scene Prompt</div>
                    <div className="text-[11px] text-text-dark">{d.scenePrompt}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ArtDirectionCard;
